import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/contexts/AuthContext'
import { ChevronDown, Edit, Eye, MoreHorizontal, Plus, Shield, Trash2 } from 'lucide-react'
import React, { useState } from 'react'
import { UserDetail } from './UserDetail'

// Add formatDate function
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return 'Unknown'
  try {
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  } catch {
    return 'Unknown'
  }
}

interface UserManagementProps {
  className?: string
  users?: {
    id: string
    name: string
    email: string
    role: string
    status?: 'active' | 'inactive'
    createdAt?: string
    phone?: string
  }[]
  isLoading?: boolean
  currentPage?: number
  totalPages?: number
  totalUsers?: number
  onPageChange?: (_page: number) => void
  onAddUser: () => void
  onEditUser: (_userId: string) => void
  onDeleteUser: (_userId: string) => void
  onResetPassword: (_userId: string) => void
  onToggleStatus: (_userId: string) => void
  onUpdateUser?: (_userId: string, _data: Record<string, unknown>) => Promise<void>
  searchTerm?: string
  setSearchTerm?: (_term: string) => void
  roleFilter?: string
  setRoleFilter?: (_role: string) => void
  statusFilter?: string
  setStatusFilter?: (_status: string) => void
}

export const UserManagement: React.FC<UserManagementProps> = ({
  className,
  users = [],
  isLoading = false,
  currentPage = 1,
  totalPages = 1,
  totalUsers,
  onPageChange,
  onAddUser,
  onEditUser,
  onDeleteUser,
  onUpdateUser,
  searchTerm = '',
  setSearchTerm = () => {},
  roleFilter = 'all',
  setRoleFilter = () => {},
  statusFilter = 'all',
  setStatusFilter = () => {},
}) => {
  const { user: currentUser } = useAuth()
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [showDetail, setShowDetail] = useState(false)

  // Remove client-side filtering since we're now filtering on the backend
  const selectedUser = users.find((u) => u.id === selectedUserId)

  const getRoleVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive'
      case 'customer':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  const getStatusVariant = (status: 'active' | 'inactive') => {
    switch (status) {
      case 'active':
        return 'default'
      case 'inactive':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId)
    setShowDetail(true)
  }

  const handleUpdateUser = async (userId: string, data: Record<string, unknown>) => {
    if (onUpdateUser) {
      await onUpdateUser(userId, data)
    }
  }

  const handleCloseDetail = () => {
    setShowDetail(false)
    setSelectedUserId(null)
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center justify-between mb-3">
            <CardTitle>User Management</CardTitle>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-[200px]"
            />
            <div className="relative">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-8 text-sm ring-offset-background appearance-none min-w-[140px]"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="customer">Customer</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none text-gray-500" />
            </div>

            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-8 text-sm ring-offset-background appearance-none min-w-[140px]"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none text-gray-500" />
            </div>

            <Button onClick={onAddUser} className="whitespace-nowrap">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <div className="mb-4 text-sm text-muted-foreground">Loading users...</div>}

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold">
                {typeof totalUsers === 'number' ? totalUsers : users.length}
              </div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Total Users
              </div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {users.filter((u) => u.status === 'active').length}
              </div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Active</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {users.filter((u) => u.role === 'admin').length}
              </div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Admins</div>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell onClick={() => handleUserClick(user.id)}>
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                          {user.phone && (
                            <div className="text-xs text-muted-foreground">{user.phone}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell onClick={() => handleUserClick(user.id)}>
                      <Badge
                        variant={getRoleVariant(user.role)}
                        className="flex items-center gap-1 w-fit"
                      >
                        <Shield className="h-3 w-3" />
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={() => handleUserClick(user.id)}>
                      {user.status ? (
                        <Badge variant={getStatusVariant(user.status)}>{user.status}</Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell onClick={() => handleUserClick(user.id)}>
                      <span className="text-sm text-muted-foreground">{formatDate(user.createdAt)}</span>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleUserClick(user.id)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onEditUser(user.id)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Quick Edit
                          </DropdownMenuItem>
                          {currentUser?.role === 'admin' && (
                            <DropdownMenuItem
                              onClick={() => onDeleteUser(user.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete User
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Empty State */}
          {users.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <div className="text-muted-foreground">No users found</div>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setSearchTerm('')
                  setRoleFilter('all')
                  setStatusFilter('all')
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={currentPage <= 1}
                  onClick={() => onPageChange && onPageChange(currentPage - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  disabled={currentPage >= totalPages}
                  onClick={() => onPageChange && onPageChange(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Detail Dialog/Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogPortal>
          <DialogOverlay />
          <div className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] gap-4 border border-gray-200 bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg max-h-[90vh] overflow-y-auto">
            <UserDetail
              user={selectedUser}
              onUpdate={handleUpdateUser}
              onClose={handleCloseDetail}
            />
          </div>
        </DialogPortal>
      </Dialog>
    </>
  )
}
